import { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import {
  Brain,
  Zap,
  Bell,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  Swords,
  BarChart2,
  TrendingUp,
  Cloud,
  AlertTriangle,
  Shield,
  PieChart,
  Target,
  TrendingDown,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAppStore } from '../store/useAppStore';
import { useOSStore, type PulseStreamTab } from '../store/useOSStore';
import type { AgentActivityEvent, IntelligenceSignal, Alert } from '../types';

// ── Agent metadata (shared with IntelligenceFeedItem) ─────────────────────────

const AGENT_META: Record<string, { label: string; colorCls: string; bgCls: string; Icon: React.FC<{ className?: string }> }> = {
  market:     { label: 'Market',    colorCls: 'text-warn',          bgCls: 'bg-warn/10 border-warn/20',     Icon: BarChart2    },
  matchup:    { label: 'Matchup',   colorCls: 'text-brand',         bgCls: 'bg-brand/10 border-brand/20',   Icon: Swords       },
  form:       { label: 'Form',      colorCls: 'text-win',           bgCls: 'bg-win/10 border-win/20',       Icon: TrendingUp   },
  context:    { label: 'Context',   colorCls: 'text-info',          bgCls: 'bg-info/10 border-info/20',     Icon: Cloud        },
  risk:       { label: 'Risk',      colorCls: 'text-warn',          bgCls: 'bg-warn/10 border-warn/20',     Icon: AlertTriangle},
  validation: { label: 'Adversarial',colorCls: 'text-loss',         bgCls: 'bg-loss/10 border-loss/20',     Icon: Shield       },
  portfolio:  { label: 'Portfolio', colorCls: 'text-brand',         bgCls: 'bg-brand/10 border-brand/20',   Icon: PieChart     },
  clv:        { label: 'CLV',       colorCls: 'text-gold',          bgCls: 'bg-gold/10 border-gold/20',     Icon: Target       },
  ml:         { label: 'ML Model',  colorCls: 'text-purple-bright', bgCls: 'bg-purple/10 border-purple/20', Icon: Brain        },
};

// ── Stream items ──────────────────────────────────────────────────────────────

function AgentStreamItem({ event }: { event: AgentActivityEvent }) {
  const meta = AGENT_META[event.agent] ?? {
    label: event.agent,
    colorCls: 'text-gray-400',
    bgCls: 'bg-surface-3 border-surface-4',
    Icon: Zap,
  };
  const { Icon, label, colorCls, bgCls } = meta;
  const hasDelta = typeof event.confidenceDelta === 'number';
  const deltaUp = hasDelta && (event.confidenceDelta ?? 0) > 0;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.16 }}
      className={clsx(
        'flex items-start gap-2 px-3 py-2 rounded-lg border',
        event.severity === 'high' && 'bg-loss/5 border-loss/15',
        event.severity === 'warn' && 'bg-warn/5 border-warn/10',
        event.severity === 'info' && 'border-transparent',
      )}
    >
      <div className={clsx('flex-shrink-0 flex items-center justify-center w-6 h-6 rounded border', bgCls)}>
        <Icon className={clsx('w-3 h-3', colorCls)} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={clsx('text-[10px] font-semibold', colorCls)}>{label}</span>
          {event.gamePk && (
            <span className="text-[9px] text-gray-600 font-mono">#{event.gamePk}</span>
          )}
        </div>
        <p className="text-[10px] text-gray-300 leading-snug mt-0.5 line-clamp-2">{event.action}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {hasDelta && (
            <span className={clsx('flex items-center gap-0.5 text-[9px] font-mono font-semibold', deltaUp ? 'text-win' : 'text-loss')}>
              {deltaUp ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
              {deltaUp ? '+' : ''}{((event.confidenceDelta ?? 0) * 100).toFixed(1)}%
            </span>
          )}
          <span className="text-[9px] text-gray-600 ml-auto tabular-nums">
            {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

function SignalStreamItem({ signal }: { signal: IntelligenceSignal }) {
  const severityColor =
    signal.severity === 'high' ? 'text-gold border-gold/20 bg-gold/5'
    : signal.severity === 'warn' ? 'text-warn border-warn/15 bg-warn/5'
    : 'text-info border-info/15 bg-info/5';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.16 }}
      className={clsx('flex items-start gap-2 px-3 py-2 rounded-lg border', severityColor)}
    >
      <Zap className="w-3 h-3 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-semibold text-gray-200 leading-snug">{signal.title}</p>
        {signal.description && (
          <p className="text-[9px] text-gray-500 leading-snug mt-0.5 line-clamp-2">{signal.description}</p>
        )}
        {signal.movement && (
          <span className="text-[9px] font-mono font-bold text-warn mt-0.5 block">{signal.movement}</span>
        )}
        <span className="text-[9px] text-gray-600 tabular-nums">
          {formatDistanceToNow(new Date(signal.timestamp), { addSuffix: true })}
        </span>
      </div>
    </motion.div>
  );
}

function AlertStreamItem({ alert }: { alert: Alert }) {
  const isUnread = !alert.is_read;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.16 }}
      className={clsx(
        'flex items-start gap-2 px-3 py-2 rounded-lg border',
        isUnread ? 'border-warn/20 bg-warn/5' : 'border-transparent',
      )}
    >
      <AlertCircle className={clsx('w-3 h-3 flex-shrink-0 mt-0.5', isUnread ? 'text-warn' : 'text-gray-500')} />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-semibold text-gray-200 leading-snug line-clamp-1">{alert.title}</p>
        {alert.body && (
          <p className="text-[9px] text-gray-400 leading-snug line-clamp-2 mt-0.5">{alert.body}</p>
        )}
        <span className="text-[9px] text-gray-600 tabular-nums">
          {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
        </span>
      </div>
      {isUnread && <span className="w-1.5 h-1.5 rounded-full bg-warn flex-shrink-0 mt-1" />}
    </motion.div>
  );
}

// ── Tab button ────────────────────────────────────────────────────────────────

function StreamTab({
  id,
  label,
  icon: Icon,
  active,
  badge,
  onClick,
}: {
  id: PulseStreamTab;
  label: string;
  icon: React.FC<{ className?: string }>;
  active: boolean;
  badge?: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'relative flex items-center gap-1 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide transition-colors rounded-md',
        active ? 'text-gray-100 bg-surface-3' : 'text-gray-500 hover:text-gray-300 hover:bg-surface-2',
      )}
    >
      <Icon className="w-3 h-3" />
      {label}
      {badge != null && badge > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-[3px] rounded-full bg-warn border border-surface-1 flex items-center justify-center text-[8px] font-bold text-surface-0 tabular-nums leading-none">
          {Math.min(badge, 99)}
        </span>
      )}
    </button>
  );
}

// ── Collapsed strip ───────────────────────────────────────────────────────────

function CollapsedStrip({ onExpand }: { onExpand: () => void }) {
  const { agentActivity } = useAppStore();
  const recentEvent = agentActivity[0];

  return (
    <div className="flex flex-col items-center justify-between h-full w-10 bg-surface-1 border-l border-surface-4 py-2">
      <button
        onClick={onExpand}
        title="Expand Intelligence Stream"
        className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-surface-2 text-gray-500 hover:text-gray-200 transition-colors"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
      </button>
      <div className="flex-1 flex flex-col items-center justify-center gap-2">
        <span className="relative flex h-1.5 w-1.5">
          {recentEvent && (
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple opacity-60" />
          )}
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-purple" />
        </span>
        <p className="text-[8px] font-bold text-purple/60 uppercase tracking-widest [writing-mode:vertical-rl] select-none">
          Live Intelligence
        </p>
      </div>
      <div className="w-8 h-8" />
    </div>
  );
}

// ── PulseStream ───────────────────────────────────────────────────────────────

export function PulseStream() {
  const { pulseStreamOpen, pulseStreamTab, togglePulseStream, setPulseStreamTab } = useOSStore();
  const { agentActivity, signals, alerts, unreadCount } = useAppStore();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll to top on new agent events
  useEffect(() => {
    if (pulseStreamOpen && pulseStreamTab === 'agents' && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [agentActivity.length, pulseStreamOpen, pulseStreamTab]);

  if (!pulseStreamOpen) {
    return <CollapsedStrip onExpand={togglePulseStream} />;
  }

  const activeSignals = signals.filter((s) => s.isActive);

  return (
    <AnimatePresence>
      <motion.div
        key="pulse-stream"
        initial={{ width: 40, opacity: 0 }}
        animate={{ width: 280, opacity: 1 }}
        exit={{ width: 40, opacity: 0 }}
        transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
        className="flex flex-col h-full bg-surface-1 border-l border-surface-4 flex-shrink-0 overflow-hidden"
        style={{ width: 280 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-surface-4 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-6 h-6 rounded-md bg-purple/10 border border-purple/20">
              <Brain className="w-3 h-3 text-purple-bright" />
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-100 leading-tight">Intelligence</p>
              <p className="text-[9px] text-purple/60 uppercase tracking-widest leading-tight">Live Stream</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {agentActivity.length > 0 && (
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple opacity-60" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-purple" />
              </span>
            )}
            <button
              onClick={togglePulseStream}
              className="p-1 text-gray-600 hover:text-gray-300 hover:bg-surface-2 rounded transition-colors"
              title="Collapse stream"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 px-2 py-1.5 border-b border-surface-4 flex-shrink-0">
          <StreamTab
            id="agents"
            label="Agents"
            icon={Brain}
            active={pulseStreamTab === 'agents'}
            badge={agentActivity.filter((e) => e.severity === 'high').length}
            onClick={() => setPulseStreamTab('agents')}
          />
          <StreamTab
            id="signals"
            label="Signals"
            icon={Zap}
            active={pulseStreamTab === 'signals'}
            badge={activeSignals.filter((s) => s.severity === 'high').length}
            onClick={() => setPulseStreamTab('signals')}
          />
          <StreamTab
            id="alerts"
            label="Alerts"
            icon={Bell}
            active={pulseStreamTab === 'alerts'}
            badge={unreadCount}
            onClick={() => setPulseStreamTab('alerts')}
          />
        </div>

        {/* Feed */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto py-1.5 space-y-0.5 px-1.5"
        >
          <AnimatePresence initial={false}>
            {pulseStreamTab === 'agents' && (
              <>
                {agentActivity.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-2 text-center px-3">
                    <Brain className="w-7 h-7 text-gray-700" />
                    <p className="text-[10px] text-gray-600 leading-snug">
                      Agent activity will appear here as analysis runs.
                    </p>
                  </div>
                ) : (
                  agentActivity.map((event) => (
                    <AgentStreamItem key={event.id} event={event} />
                  ))
                )}
              </>
            )}

            {pulseStreamTab === 'signals' && (
              <>
                {activeSignals.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-2 text-center px-3">
                    <Zap className="w-7 h-7 text-gray-700" />
                    <p className="text-[10px] text-gray-600 leading-snug">
                      Market signals will stream here in real-time.
                    </p>
                  </div>
                ) : (
                  activeSignals.map((signal) => (
                    <SignalStreamItem key={signal.id} signal={signal} />
                  ))
                )}
              </>
            )}

            {pulseStreamTab === 'alerts' && (
              <>
                {alerts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-2 text-center px-3">
                    <Bell className="w-7 h-7 text-gray-700" />
                    <p className="text-[10px] text-gray-600 leading-snug">
                      No alerts yet.
                    </p>
                  </div>
                ) : (
                  alerts.slice(0, 30).map((alert) => (
                    <AlertStreamItem key={alert.id} alert={alert} />
                  ))
                )}
              </>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-surface-4 px-3 py-2">
          <a
            href="/pulse/center"
            className="flex items-center justify-between w-full text-[10px] text-gray-600 hover:text-gray-300 transition-colors group"
          >
            <span>Intelligence command</span>
            <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── Toggle button (for use in toolbar areas) ──────────────────────────────────

export function PulseStreamToggle({ className }: { className?: string }) {
  const { pulseStreamOpen, togglePulseStream } = useOSStore();
  const { agentActivity } = useAppStore();
  const highCount = agentActivity.filter((e) => e.severity === 'high').length;

  return (
    <button
      onClick={togglePulseStream}
      title="Intelligence Stream"
      className={clsx(
        'relative flex items-center justify-center w-8 h-8 rounded-lg transition-colors',
        pulseStreamOpen
          ? 'bg-purple/15 text-purple-bright border border-purple/30'
          : 'text-gray-400 hover:text-gray-200 hover:bg-surface-3 border border-transparent',
        className,
      )}
    >
      <Brain className="w-4 h-4" />
      {highCount > 0 && !pulseStreamOpen && (
        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-loss border border-surface-1" />
      )}
    </button>
  );
}
